import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import {
  Search, RefreshCw, ChevronLeft, ChevronDown, Check,
  Link2, Clock, Filter, X, LayoutGrid, List, Eye,
} from "lucide-react";
import { format, subDays, startOfDay, startOfYesterday, parseISO } from "date-fns";
import {
  setNewsStoriesCache,
  getStoriesForDate,
  getNewsStoriesLastRefreshed,
  getRelativeTime,
} from "@/lib/cache";
import { CompanyLogo } from "@/components/CompanyLogo";
import { NewsTable, getImpactLevel, isAttentionStory } from "@/components/NewsTable";
import { getSentiment, getSimplifiedSentiment } from "@/utils/sentiment";

const FILTER_SESSION_KEY = "wokelo_watchlist_filters";
const VIEW_KEY            = "wokelo_watchlist_view";
const SORT_KEY            = "wokelo_watchlist_sort";
const PAGE_SIZE_CARDS     = 12;
const PAGE_SIZE_TABLE     = 20;

const TIME_PERIODS = [
  { label: "All",       id: "all",       days: 60 },
  { label: "Today",     id: "today",     days: 0 },
  { label: "Yesterday", id: "yesterday", days: 1 },
  { label: "3 Days",    id: "3days",     days: 3 },
  { label: "7 Days",    id: "7days",     days: 7 },
  { label: "30 Days",   id: "30days",    days: 30 },
  { label: "60 Days",   id: "60days",    days: 60 },
  { label: "Custom",    id: "custom",    days: 60 },
];

const NEWS_CATEGORIES = [
  "Deals", "Valuations", "Leadership", "Governance", "Macroeconomic",
  "Earnings Results", "Regulatory / Policy", "Strategy Updates",
  "Operational Updates", "Competitive Dynamics", "Market / Industry Trends",
  "Capital Allocation", "Capital Structure / Financing", "Exit & Liquidity",
];

// Raw API tag → curated category mapping (for primary_tags that don't match curated list)
const RAW_TO_CURATED = {
  "general news": null,
  "investment or funding related news": "Capital Allocation",
  "investment or funding": "Capital Allocation",
  "investment": "Capital Allocation",
  "funding": "Capital Allocation",
  "venture capital": "Capital Allocation",
  "market update": "Market / Industry Trends",
  "market news": "Market / Industry Trends",
  "industry trends": "Market / Industry Trends",
  "earnings": "Earnings Results",
  "earnings report": "Earnings Results",
  "quarterly results": "Earnings Results",
  "financial results": "Earnings Results",
  "regulatory": "Regulatory / Policy",
  "policy": "Regulatory / Policy",
  "regulation": "Regulatory / Policy",
  "merger": "Deals",
  "acquisition": "Deals",
  "m&a": "Deals",
  "deal": "Deals",
  "ipo": "Exit & Liquidity",
  "exit": "Exit & Liquidity",
  "listing": "Exit & Liquidity",
  "leadership change": "Leadership",
  "management change": "Leadership",
  "ceo": "Leadership",
  "strategy": "Strategy Updates",
  "operational": "Operational Updates",
  "competitive": "Competitive Dynamics",
  "capital structure": "Capital Structure / Financing",
  "financing": "Capital Structure / Financing",
  "debt": "Capital Structure / Financing",
  "governance": "Governance",
  "valuation": "Valuations",
  "macroeconomic": "Macroeconomic",
  "macro": "Macroeconomic",
};

const PORTFOLIO_IMPACT_LEVELS = ["Low", "Medium", "High", "Critical"];
const SENTIMENT_OPTIONS = ["POSITIVE", "NEUTRAL", "NEGATIVE"];
const GEO_REGIONS = ["North America", "Europe", "APAC", "LATAM", "MEA"];

const INDUSTRY_VERTICALS = [
  "AI / Tech", "E-Commerce", "Biotech / Health", "Fintech", "Deep Tech",
  "SaaS / B2B", "Consumer", "Energy", "Industrial", "Defence", "Real Estate",
];

const INDUSTRY_KEYWORDS = {
  "AI / Tech":        /\b(artificial intelligence|machine learning|deep learning|llm|gpu|semiconductor|cloud computing|data center|openai|google ai|microsoft ai|meta ai|nvidia|bytedance|scale.ai|cohere|perplexity|glean|arm holdings)\b/i,
  "E-Commerce":       /\b(e.commerce|ecommerce|online retail|marketplace|food delivery|last.mile delivery|doordash|coupang|flipkart|amazon|shopify|instacart|grubhub)\b/i,
  "Biotech / Health":  /\b(biotech|pharmaceutical|drug discovery|clinical trial|medical device|hospital|healthcare|gene therapy|genomic|vaccine|fda approval|metsera|recursion|glp.1|oncology)\b/i,
  "Fintech":          /\b(fintech|digital payment|neobank|insurtech|digital lending|credit card|digital wallet|stripe|brex|ramp|checkout\.com|blockchain|cryptocurrency|bitcoin)\b/i,
  "Deep Tech":        /\b(quantum computing|nuclear fusion|robotics|autonomous vehicle|self.driving|waymo|helion|satellite|lidar|3d printing|advanced materials|superconductor)\b/i,
  "SaaS / B2B":       /\b(saas|enterprise software|crm platform|erp system|cloud service|subscription software|databricks|snowflake|salesforce|servicenow|wiz|anduril)\b/i,
  "Consumer":         /\b(consumer goods|consumer brand|consumer product|cpg|fmcg|food and beverage|fashion brand|apparel|restaurant chain|hospitality|hotel chain|travel booking|tourism|oyo rooms|travelperk|airbnb|uber)\b/i,
  "Energy":           /\b(oil and gas|lng terminal|crude oil|petroleum|solar energy|wind energy|renewable energy|battery storage|electric vehicle|power grid|carbon emission|climate change|vertical farm|fertilizer)\b/i,
  "Industrial":       /\b(industrial manufacturing|supply chain|freight shipping|port infrastructure|construction|mining|steel production|cement|aerospace|aviation|airline)\b/i,
  "Defence":          /\b(defence|defense contract|military|weapon system|missile|nato|pentagon|armed forces|navy|air force|surveillance|cybersecurity)\b/i,
  "Real Estate":      /\b(real estate|property market|housing|mortgage|commercial real estate|reit|office space|residential|pacific union|redfin|zillow|clutter)\b/i,
};

const COMPANY_INDUSTRY_MAP = {
  "openai": ["AI / Tech"], "perplexity": ["AI / Tech"], "glean": ["AI / Tech"], "cohere": ["AI / Tech"],
  "scale ai": ["AI / Tech"], "bytedance": ["AI / Tech"], "arm holdings": ["AI / Tech"], "arm": ["AI / Tech"],
  "doordash": ["E-Commerce"], "coupang": ["E-Commerce"], "flipkart": ["E-Commerce"], "grabwheels": ["E-Commerce"],
  "metsera": ["Biotech / Health"], "recursion": ["Biotech / Health"],
  "stripe": ["Fintech"], "brex": ["Fintech"], "ramp": ["Fintech"],
  "waymo": ["Deep Tech"], "helion": ["Deep Tech"],
  "wiz": ["SaaS / B2B"], "anduril": ["SaaS / B2B", "Defence"],
  "oyo": ["Consumer"], "travelperk": ["Consumer"],
  "plenty": ["Energy"], "plenty unlimited": ["Energy"],
  "pacific union": ["Real Estate"], "clutter": ["Real Estate"],
};

const PUBLISHER_TYPES = [
  "Financial Media", "Trade Media", "Press Release", "Analyst Report", "Regulatory Filing",
];

const SORT_OPTIONS = [
  { id: "risk_priority", label: "Risk Priority" },
  { id: "latest_first",  label: "Latest First"  },
  { id: "company_first", label: "Company First"  },
  { id: "industry_first", label: "Industry First" },
];

const toDateKey = (date) => format(date, "yyyy-MM-dd");

// Normalize string for robust tag comparison (handle unicode spaces, trim)
const normStr = (s) => s ? String(s).replace(/[\u00A0\s]+/g, " ").trim() : "";

// vertical_tag comes as a JSON array from the API — parse correctly
function parseVerticalTags(verticalTag) {
  if (!verticalTag) return [];
  if (Array.isArray(verticalTag)) return verticalTag.map(t => (t || "").trim()).filter(Boolean);
  return normStr(String(verticalTag)).split(",").map(t => t.trim()).filter(Boolean);
}

function mapNewsCategory(primaryTag) {
  const pt = normStr(primaryTag || "");
  const base = pt.split(",").map(t => t.trim().includes(" - ") ? t.trim().split(" - ")[0].trim() : t.trim()).filter(Boolean)[0] || "";
  if (!base) return null;
  // 1. Exact match against curated list
  const exact = NEWS_CATEGORIES.find(c => c.toLowerCase() === base.toLowerCase());
  if (exact) return exact;
  // 2. Raw→curated mapping
  const rawKey = base.toLowerCase().trim();
  if (rawKey in RAW_TO_CURATED) return RAW_TO_CURATED[rawKey];
  // 3. Partial/contains match
  for (const [raw, curated] of Object.entries(RAW_TO_CURATED)) {
    if (rawKey.includes(raw) || raw.includes(rawKey)) return curated;
  }
  return null;
}

function getDateRange(periodId) {
  const now = new Date();
  switch (periodId) {
    case "today":     return format(startOfDay(now), "yyyy-MM-dd'T'HH:mm:ss");
    case "yesterday": return format(startOfYesterday(), "yyyy-MM-dd'T'HH:mm:ss");
    case "3days":     return format(startOfDay(subDays(now, 2)), "yyyy-MM-dd'T'HH:mm:ss");
    case "7days":     return format(startOfDay(subDays(now, 6)), "yyyy-MM-dd'T'HH:mm:ss");
    case "30days":    return format(startOfDay(subDays(now, 30)), "yyyy-MM-dd'T'HH:mm:ss");
    default:          return format(startOfDay(now), "yyyy-MM-dd'T'HH:mm:ss");
  }
}

function formatTimeAgo(dateString) {
  if (!dateString) return "";
  try {
    const date = parseISO(dateString);
    const now = new Date();
    const diffMins  = Math.floor((now - date) / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays  = Math.floor(diffHours / 24);
    if (diffMins < 60)  return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7)   return `${diffDays}d ago`;
    return format(date, "MMM d");
  } catch { return ""; }
}

// ─── Sentiment Badge ──────────────────────────────────────────────────────────
const SENTIMENT_STYLES = {
  POSITIVE:   { bg: "#DCFCE7", color: "#16A34A" },
  NEGATIVE:   { bg: "#FEE2E2", color: "#DC2626" },
  NEUTRAL:    { bg: "#F3F4F6", color: "#6B7280" },
  CAUTIONARY: { bg: "#FEF3C7", color: "#D97706" },
  MIXED:      { bg: "#DBEAFE", color: "#2563EB" },
};

function SentimentBadge({ sentiment, loading = false, tooltip = null }) {
  if (loading) return <span className="inline-block h-4 w-16 rounded-full animate-pulse bg-[#E5E7EB]" data-testid="sentiment-loading" />;
  if (!sentiment) return null;
  const style = SENTIMENT_STYLES[sentiment] || SENTIMENT_STYLES.NEUTRAL;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium cursor-default"
      style={{ background: style.bg, color: style.color }}
      title={tooltip || undefined}
      data-testid={`sentiment-badge-${sentiment}`}>
      {sentiment}
    </span>
  );
}

// ─── Sort Dropdown ────────────────────────────────────────────────────────────
function SortDropdown({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const current = options.find(o => o.id === selected) || options[0];
  return (
    <div ref={ref} className="relative" data-testid="sort-dropdown">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-3 h-[34px] bg-white rounded-md text-[12px] font-medium text-[#3A3A3A] hover:bg-[#F4F5F6] transition-colors"
        style={{ border: "1px solid #DCDFE2" }}
        data-testid="sort-dropdown-btn"
      >
        <span className="text-[10px] text-[#9AA0A6]">Sort</span>
        <span className="text-[#DCDFE2] text-[10px]">|</span>
        <span>{current.label}</span>
        <ChevronDown size={11} className={`text-[#9AA0A6] transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 right-0 bg-white rounded-md z-50"
          style={{ border: "1px solid #E5E7EB", boxShadow: "0 4px 16px rgba(0,0,0,0.10)", minWidth: "170px" }}>
          {options.map(opt => (
            <button key={opt.id}
              onClick={() => { onChange(opt.id); setOpen(false); }}
              className="w-full flex items-center justify-between px-3 py-2 text-[12px] transition-colors hover:bg-[#F4F5F6] first:rounded-t-lg last:rounded-b-lg"
              style={{ color: selected === opt.id ? "#60A5FA" : "#3A3A3A", fontWeight: selected === opt.id ? 600 : 400 }}
              data-testid={`sort-option-${opt.id}`}
            >
              {opt.label}
              {selected === opt.id && <Check size={11} className="text-[#60A5FA]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Unified Story Card ───────────────────────────────────────────────────────
function StoryCard({ story, onClick, portfolioSentiment }) {
  const navigate = useNavigate();
  const hasPortfolio = story.portfolio_impact && story.portfolio_impact.length > 0;
  const portfolioCompaniesData = hasPortfolio
    ? Object.values(story.portfolio_impact.reduce((acc, p) => {
        if (p.company_name && !acc[p.company_name]) {
          acc[p.company_name] = {
            name: p.company_name,
            permalink: p.company_permalink || p.company_name.toLowerCase().replace(/\s+/g, "-"),
          };
        }
        return acc;
      }, {}))
    : [];
  const portfolioCompanies = portfolioCompaniesData.map(c => c.name);
  // Always compute keyword sentiment — used directly for non-portfolio, as fallback for portfolio
  const keywordSentiment = getSimplifiedSentiment(getSentiment(`${story.title || ""} ${story.snippet || ""}`));
  const displaySentiment = hasPortfolio ? getSimplifiedSentiment(portfolioSentiment?.value ?? keywordSentiment) : keywordSentiment;
  const sentimentLoading  = hasPortfolio && portfolioSentiment?.loading === true && !keywordSentiment;
  const sentimentTooltip  = hasPortfolio ? "Watchlist impact sentiment" : "Article sentiment";

  return (
    <article
      className="bg-white rounded-md p-5 cursor-pointer transition-shadow duration-200 hover:shadow-md"
      style={hasPortfolio
        ? { borderLeft: "6px solid #60A5FA", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }
        : { border: "1px solid #DCDFE2", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}
      onClick={() => onClick(story)}
      data-testid={`watchlist-story-card-${story.brief_id}`}
    >
      <div className="flex gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {portfolioCompanies.length > 0 && (
              <div className="inline-flex items-center gap-2 flex-wrap">
                {portfolioCompaniesData.slice(0, 2).map((c, idx) => (
                  <button
                    key={c.name}
                    onClick={e => { e.stopPropagation(); navigate(`/watchlist-matrix?company=${encodeURIComponent(c.name)}`); }}
                    className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                  >
                    <CompanyLogo name={c.name} permalink={c.permalink} size={24} />
                    <span className="text-sm font-semibold text-gray-900">{c.name}</span>
                    {idx < portfolioCompaniesData.slice(0, 2).length - 1 && <span className="text-gray-400 text-xs">·</span>}
                  </button>
                ))}
                {portfolioCompanies.length > 2 && (
                  <span className="text-[11px] text-[#9AA0A6]">+{portfolioCompanies.length - 2} more</span>
                )}
              </div>
            )}
            {/* News Category badge (mapped from primary_tag) */}
            {(() => {
              const matched = mapNewsCategory(story.primary_tag);
              return matched ? (
                <span className="text-[10px] font-medium text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded-full">{matched}</span>
              ) : null;
            })()}
            {/* Industry badges (mapped via keyword + company matching) */}
            {(() => {
              const industries = getStoryIndustries(story);
              return industries.map(ind => (
                <span key={ind} className="text-[10px] font-medium text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded-full">{ind}</span>
              ));
            })()}
            <div className="ml-auto flex-shrink-0">
              <SentimentBadge sentiment={displaySentiment} loading={sentimentLoading} tooltip={sentimentTooltip} />
            </div>
          </div>
          <h3 className="font-semibold text-[#0A0A0A] leading-snug mb-2 line-clamp-2"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: "15px" }}>
            {story.title}
          </h3>
          <p className="text-sm text-[#4B5563] line-clamp-2 mb-3">
            {story.snippet || story.description?.slice(0, 150)}
          </p>
          <div className="flex items-center justify-between text-[11px] text-[#9AA0A6]">
            <span className="flex items-center gap-1"><Link2 size={11} />{getSmartSourceDisplay(story) || `${story.source_count || 0} sources`}</span>
            <span className="flex items-center gap-1"><Clock size={11} />{formatTimeAgo(story.first_seen_at || story.updated_at)}</span>
          </div>
        </div>
        {story.image_url && (
          <div className="flex-shrink-0">
            <img src={story.image_url} alt="" className="w-[90px] h-[70px] object-cover rounded-md"
              onError={e => { e.target.parentElement.style.display = "none"; }} />
          </div>
        )}
      </div>
      {hasPortfolio && portfolioSentiment?.reason && (
        <p className="text-[11px] text-[#9AA0A6] italic mt-2 pt-2" style={{ borderTop: "1px solid #F0F2F4" }}>
          {portfolioSentiment.reason}
        </p>
      )}
    </article>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function StoryCardSkeleton() {
  return (
    <div className="bg-white rounded-md border border-[#DCDFE2] p-5">
      <div className="flex gap-4">
        <div className="flex-1">
          <div className="skeleton h-4 w-24 mb-3" />
          <div className="skeleton h-5 w-full mb-2" />
          <div className="skeleton h-5 w-3/4 mb-3" />
          <div className="skeleton h-3 w-full mb-1" />
          <div className="skeleton h-3 w-2/3 mb-3" />
          <div className="flex justify-between">
            <div className="skeleton h-3 w-20" />
            <div className="skeleton h-3 w-16" />
          </div>
        </div>
        <div className="skeleton w-[90px] h-[70px] rounded-lg flex-shrink-0" />
      </div>
    </div>
  );
}

// ─── Table View ───────────────────────────────────────────────────────────────
function TableView({ stories, onStoryClick, getPortfolioSentiment }) {
  const [page, setPage] = useState(0);
  const [expandedImpact, setExpandedImpact] = useState({});
  const totalPages = Math.ceil(stories.length / PAGE_SIZE_TABLE);
  const rows = stories.slice(page * PAGE_SIZE_TABLE, (page + 1) * PAGE_SIZE_TABLE);

  return (
    <div>
      <div className="bg-white rounded-md overflow-hidden"
        style={{ border: "1px solid #E5E7EB", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <table className="w-full" data-testid="table-view">
          <thead>
            <tr style={{ background: "#F5F5F5", borderBottom: "1px solid #DCDFE2" }}>
              {[["Headline", "36%"], ["Sentiment", "10%"], ["Watchlist", "15%"], ["Impact", "240px"], ["Sources", "12%"], ["Time", "9%"]].map(([label, w]) => (
                <th key={label} className="text-left px-4 py-3 text-xs font-semibold uppercase"
                  style={{ width: w, letterSpacing: "0.07em", color: "#707070" }}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((story, idx) => {
              // Build portfolio companies with logo data (Fix 5)
              const portfolioCompaniesData = story.portfolio_impact?.length
                ? Object.values(story.portfolio_impact.reduce((acc, p) => {
                    if (p.company_name && !acc[p.company_name]) {
                      acc[p.company_name] = {
                        name: p.company_name,
                        permalink: p.company_permalink || p.company_name.toLowerCase().replace(/\s+/g, "-"),
                        value: p.value || p.impact_description || "",
                      };
                    }
                    return acc;
                  }, {}))
                : [];
              const hasPortfolio = portfolioCompaniesData.length > 0;

              let sentimentVal, sentimentLoading, sentimentTooltip;
              const kwSentiment = getSimplifiedSentiment(getSentiment(`${story.title || ""} ${story.snippet || ""}`));
              if (hasPortfolio) {
                const ps = getPortfolioSentiment(story);
                sentimentVal     = getSimplifiedSentiment(ps?.value ?? kwSentiment);
                sentimentLoading = ps?.loading === true && !kwSentiment;
                sentimentTooltip = "Watchlist impact sentiment";
              } else {
                sentimentVal     = kwSentiment;
                sentimentLoading = false;
                sentimentTooltip = "Article sentiment";
              }

              // Impact column data
              const impactKey = story.brief_id;
              const isImpactExpanded = expandedImpact[impactKey];
              const firstImpact = portfolioCompaniesData[0]?.value || "";
              const extraImpactCount = portfolioCompaniesData.length - 1;
              const impactTruncated = firstImpact.length > 60 ? firstImpact.slice(0, 60) + "…" : firstImpact;

              return (
                <tr key={story.brief_id} onClick={() => onStoryClick(story)}
                  className="cursor-pointer transition-colors"
                  style={{ borderBottom: idx < rows.length - 1 ? "1px solid #E5E7EB" : "none", background: idx % 2 === 0 ? "#FFFFFF" : "#F9F9F9" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#E9F1EC"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = idx % 2 === 0 ? "#FFFFFF" : "#F9F9F9"; }}
                  data-testid={`table-row-${story.brief_id}`}
                >
                  <td className="px-4 py-3">
                    <p className="text-[13px] font-semibold text-[#1A1A1A] line-clamp-2 leading-snug hover:underline"
                      style={{ fontFamily: "'Playfair Display', serif" }}>
                      {story.title}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <SentimentBadge sentiment={sentimentVal} loading={sentimentLoading} tooltip={sentimentTooltip} />
                  </td>
                  {/* Watchlist column: logo + name, no red */}
                  <td className="px-4 py-3">
                    {portfolioCompaniesData.length > 0 ? (
                      <div className="flex flex-col gap-1.5">
                        {portfolioCompaniesData.slice(0, 2).map(c => (
                          <div key={c.name} className="flex items-center gap-1.5">
                          <CompanyLogo name={c.name} permalink={c.permalink} size={24} />
                            <span className="text-sm font-semibold text-gray-900">{c.name}</span>
                          </div>
                        ))}
                        {portfolioCompaniesData.length > 2 && (
                          <span className="text-[10px] text-[#9AA0A6]">+{portfolioCompaniesData.length - 2} more</span>
                        )}
                      </div>
                    ) : <span className="text-[11px] text-[#9AA0A6]">—</span>}
                  </td>
                  {/* Impact column */}
                  <td className="px-4 py-3" style={{ minWidth: "240px", maxWidth: "240px" }} onClick={e => e.stopPropagation()}>
                    {firstImpact ? (
                      <div>
                        {isImpactExpanded ? (
                          <>
                            <span className="text-xs text-gray-600 leading-relaxed">{firstImpact}</span>
                            {extraImpactCount > 0 && portfolioCompaniesData.slice(1).map(c => (
                              c.value ? <div key={c.name} className="text-xs text-gray-600 leading-relaxed mt-1">{c.value}</div> : null
                            ))}
                            <button
                              onClick={() => setExpandedImpact(p => ({ ...p, [impactKey]: false }))}
                              className="text-[10px] text-[#60A5FA] hover:underline block mt-1"
                            >↑ Collapse</button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => setExpandedImpact(p => ({ ...p, [impactKey]: true }))}
                              className="text-xs text-gray-600 leading-relaxed text-left hover:text-[#60A5FA] transition-colors"
                            >
                              {impactTruncated}
                              {(firstImpact.length > 60 || extraImpactCount > 0) && (
                                <span className="text-[10px] text-[#60A5FA] ml-1">
                                  {extraImpactCount > 0 ? `+${extraImpactCount} more` : "more"}
                                </span>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    ) : <span className="text-[11px] text-[#9AA0A6]">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] text-[#6B7280]">
                      {getSmartSourceDisplay(story) || (story.source_count || 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] text-[#9AA0A6]">
                      {formatTimeAgo(story.first_seen_at || story.updated_at)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3"
            style={{ borderTop: "1px solid #F0F2F4", background: "#FFFFFF" }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-40 transition-colors hover:bg-[#F4F5F6]"
              style={{ border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#3A3A3A" }}
              data-testid="table-prev-page">
              Previous
            </button>
            <span className="text-[12px] text-[#9AA0A6]">
              {stories.length} stories · Page {page + 1} of {totalPages}
            </span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="px-3 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-40 transition-colors hover:bg-[#F4F5F6]"
              style={{ border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#3A3A3A" }}
              data-testid="table-next-page">
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tag List (clean text-based filter list, replaces TagPillCloud) ───────────
function TagList({ tags, selected, onToggle, testPrefix = "tag" }) {
  if (!tags || tags.length === 0) {
    return <p style={{ fontSize: "11px", color: "#8C877F", fontStyle: "italic", padding: "4px 0" }}>None</p>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
      {tags.map(tag => {
        const isActive = selected.includes(tag);
        return (
          <label key={tag}
            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isActive ? "#E9F1EC" : "transparent", transition: "background 100ms" }}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#F0EDE8"; }}
            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
            data-testid={`${testPrefix}-${tag.replace(/[\s/]+/g, "-")}`}
          >
            <input type="checkbox" checked={isActive} onChange={() => onToggle(tag)}
              style={{ width: "14px", height: "14px", flexShrink: 0, cursor: "pointer", accentColor: "#1A1A1A" }} />
            <span style={{ fontSize: "12px", color: isActive ? "#1A1A1A" : "#6B7280", fontWeight: isActive ? 500 : 400, flex: 1 }}>{tag}</span>
          </label>
        );
      })}
    </div>
  );
}

// ─── Sentiment List (colored dots) ───────────────────────────────────────────
const SENT_DOT = {
  POSITIVE:   "#16A34A",
  NEGATIVE:   "#DC2626",
  CAUTIONARY: "#D97706",
  NEUTRAL:    "#9AA0A6",
  MIXED:      "#2563EB",
};
function SentimentList({ sentiments, selected, onToggle }) {
  if (!sentiments || sentiments.length === 0) {
    return <p style={{ fontSize: "11px", color: "#8C877F", fontStyle: "italic", padding: "4px 0" }}>None available</p>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
      {sentiments.map(s => {
        const isActive = selected.includes(s);
        return (
          <label key={s}
            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isActive ? "#E9F1EC" : "transparent", transition: "background 100ms" }}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#F0EDE8"; }}
            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
            data-testid={`sentiment-${s}`}
          >
            <input type="checkbox" checked={isActive} onChange={() => onToggle(s)}
              style={{ width: "14px", height: "14px", flexShrink: 0, cursor: "pointer", accentColor: "#1A1A1A" }} />
            <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: SENT_DOT[s] || "#9AA0A6", flexShrink: 0 }} />
            <span style={{ fontSize: "12px", color: isActive ? "#1A1A1A" : "#6B7280", fontWeight: isActive ? 500 : 400, flex: 1 }}>
              {s.charAt(0) + s.slice(1).toLowerCase()}
            </span>
          </label>
        );
      })}
    </div>
  );
}
// ─── Geography filter constants + logic ──────────────────────────────────────
const REGION_KEYWORDS = {
  "North America": /\b(us|u\.s\.|united states|american|federal reserve|\bfed\b|wall street|washington|canada|canadian|mexico|mexican)\b/i,
  "Europe": /\b(europe|european|eu\b|uk\b|britain|british|london|germany|german|france|french|spain|italy|swiss|switzerland|netherlands|nordic|scandinavian|ireland|portugal|poland|austria|belgium|bank of england|ecb)\b/i,
  "APAC": /\b(china|chinese|beijing|shanghai|\bccp\b|japan|japanese|tokyo|nikkei|india|indian|mumbai|korea|korean|taiwan|singapore|australia|australian|southeast asia|hong kong|asean|vietnam|indonesia|philippines|new zealand)\b/i,
  "LATAM": /\b(brazil|brazilian|argentina|chile|colombia|mexico|latin america|south america|peru|venezuela|latam)\b/i,
  "MEA": /\b(middle east|saudi|uae|dubai|qatar|israel|africa|african|nigeria|south africa|egypt|iran|iraq|turkey|turkish)\b/i,
};

function matchesGeoRegion(story, region) {
  if (!region) return true;
  const text = `${story.title || ""} ${story.snippet || ""} ${story.description || ""}`;
  const tags = [
    ...normStr(story.primary_tag || "").split(","),
    ...(Array.isArray(story.vertical_tag) ? story.vertical_tag : normStr(story.vertical_tag || "").split(",")),
  ].map(t => t.trim().toLowerCase()).join(" ");
  const combined = text + " " + tags;
  const regex = REGION_KEYWORDS[region];
  return regex ? regex.test(combined) : true;
}

function matchesGeoCountry(story, country) {
  if (!country) return true;
  const text = `${story.title || ""} ${story.snippet || ""} ${story.description || ""}`;
  const tags = [
    ...normStr(story.primary_tag || "").split(","),
    ...(Array.isArray(story.vertical_tag) ? story.vertical_tag : normStr(story.vertical_tag || "").split(",")),
  ].map(t => t.trim().toLowerCase()).join(" ");
  return (text + " " + tags).toLowerCase().includes(country.toLowerCase());
}

function getStoryIndustries(story) {
  const text = `${story.title || ""} ${story.snippet || ""}`;
  const vtags = parseVerticalTags(story.vertical_tag).join(" ");
  const companies = (story.portfolio_impact || []).map(p => p.company_name || "");
  const combined = `${text} ${vtags}`;
  const matched = new Set();
  for (const companyName of companies) {
    const key = companyName.toLowerCase().trim();
    const industries = COMPANY_INDUSTRY_MAP[key];
    if (industries) industries.forEach(ind => matched.add(ind));
  }
  for (const [vertical, regex] of Object.entries(INDUSTRY_KEYWORDS)) {
    if (regex.test(combined)) matched.add(vertical);
  }
  return [...matched];
}

// ─── Collapsible Filter Section ───────────────────────────────────────────────
function FilterSection({ title, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: "4px" }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 6px", border: "none", background: "transparent", cursor: "pointer",
        }}
      >
        <span style={{ fontSize: "11px", fontWeight: 700, color: "#6B6560", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {title} {count > 0 && <span style={{ color: "#60A5FA" }}>· {count}</span>}
        </span>
        <ChevronDown size={12} style={{ color: "#9AA0A6", transition: "transform 150ms", transform: open ? "rotate(180deg)" : "rotate(0)" }} />
      </button>
      {open && <div style={{ padding: "0 0 8px" }}>{children}</div>}
      <div style={{ height: "1px", background: "#E8E4DF" }} />
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function BriefingDashboard() {
  const navigate = useNavigate();
  const [stories,       setStories]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [error,         setError]         = useState("");
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) || "table"; } catch { return "table"; }
  });
  const [sortOrder, setSortOrder] = useState(() => {
    try { return localStorage.getItem(SORT_KEY) || "risk_priority"; } catch { return "risk_priority"; }
  });

  // Card view pagination (Fix 3) — reset on filter/sort changes
  const [cardPage, setCardPage] = useState(0);

  // Portfolio sentiment (Tier A)
  const [portfolioSentiments, setPortfolioSentiments] = useState({});
  const sentimentFetchedRef = useRef(new Set());

  const isMobile = () => typeof window !== "undefined" && window.innerWidth < 768;
  const [filterCollapsed, setFilterCollapsed] = useState(() => {
    if (isMobile()) return true;
    try { return localStorage.getItem("wokelo_watchlist_filter_collapsed") === "true"; } catch { return false; }
  });
  const toggleFilterPanel = () => setFilterCollapsed(v => {
    const next = !v;
    if (!isMobile()) { try { localStorage.setItem("wokelo_watchlist_filter_collapsed", String(next)); } catch {} }
    return next;
  });

  // Restore filters
  const saved = useMemo(() => {
    try { const s = sessionStorage.getItem(FILTER_SESSION_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
  }, []); // eslint-disable-line

  const [search,               setSearch]               = useState(saved?.search || "");
  const [selectedCompanies,    setSelectedCompanies]    = useState(saved?.selectedCompanies || []);
  const [selectedImpactLevels, setSelectedImpactLevels] = useState(saved?.selectedImpactLevels || []);
  const [selectedFlaggedCos,   setSelectedFlaggedCos]   = useState(saved?.selectedFlaggedCos || []);
  const [selectedSentiments,   setSelectedSentiments]   = useState(saved?.selectedSentiments || []);
  const [selectedCategories,   setSelectedCategories]   = useState(saved?.selectedCategories || []);
  const [selectedGeoRegion,    setSelectedGeoRegion]    = useState(saved?.selectedGeoRegion || "");
  const [selectedGeoCountry,   setSelectedGeoCountry]   = useState(saved?.selectedGeoCountry || "");
  const [selectedPublishers,   setSelectedPublishers]   = useState(saved?.selectedPublishers || []);
  const [selectedPubTypes,     setSelectedPubTypes]     = useState(saved?.selectedPubTypes || []);
  const [selectedIndustries,   setSelectedIndustries]   = useState(saved?.selectedIndustries || []);
  const [customDateStart,      setCustomDateStart]      = useState(saved?.customDateStart || "");
  const [customDateEnd,        setCustomDateEnd]        = useState(saved?.customDateEnd || "");
  const [timePeriod, setTimePeriod] = useState(() => {
    if (saved?.timePeriodId) { const f = TIME_PERIODS.find(p => p.id === saved.timePeriodId); if (f) return f; }
    return TIME_PERIODS[0];
  });

  // Flagged companies fetched from API
  const [flaggedCompanyList, setFlaggedCompanyList] = useState([]);
  useEffect(() => {
    fetch(`${API}/companies/flagged`).then(r => r.json())
      .then(data => setFlaggedCompanyList((data.flagged_companies || []).map(fc => ({ id: fc.company_id, name: fc.company_name }))))
      .catch(() => {});
  }, []);

  // Ref so fetchStories can read current period without stale closure
  const timePeriodRef = useRef(timePeriod);
  useEffect(() => { timePeriodRef.current = timePeriod; }, [timePeriod]);

  // Persist filters to session
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_SESSION_KEY, JSON.stringify({
        search, selectedCompanies, selectedImpactLevels, selectedFlaggedCos,
        selectedSentiments, selectedCategories, selectedGeoRegion, selectedGeoCountry,
        selectedPublishers, selectedPubTypes, selectedIndustries,
        customDateStart, customDateEnd, timePeriodId: timePeriod.id,
      }));
    } catch {}
  }, [search, selectedCompanies, selectedImpactLevels, selectedFlaggedCos, selectedSentiments, selectedCategories, selectedGeoRegion, selectedGeoCountry, selectedPublishers, selectedPubTypes, selectedIndustries, customDateStart, customDateEnd, timePeriod]);

  // Persist view and sort
  useEffect(() => { try { localStorage.setItem(SORT_KEY, sortOrder); } catch {} }, [sortOrder]);

  // ── Fetch stories (Fix 2: force refresh on period change) ──────────────────
  const fetchStories = useCallback(async (force = false, overridePeriod = null) => {
    setLoading(true);
    setError("");
    try {
      const effectivePeriod = overridePeriod !== null ? overridePeriod : timePeriodRef.current;
      // Cache key is per-day + per-period so period changes don't serve stale data
      const dateKey = `${toDateKey(new Date())}_${effectivePeriod.id}`;
      if (!force) {
        const cached = getStoriesForDate(dateKey);
        if (cached && cached.length > 0) {
          setStories(cached);
          setLastRefreshed(getNewsStoriesLastRefreshed());
          setLoading(false);
          return;
        }
      }
      // Fetch 30-day window so backend has enough stories to reach 500; client-side time filter trims to selected period
      const updatedAfter = getDateRange("30days");
      const res = await fetch(`${API}/stories/bulk?updated_after=${updatedAfter}&max_stories=500`);
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `API error: ${res.status}`); }
      const data = await res.json();
      const items = data.data || [];
      items.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
      setNewsStoriesCache(dateKey, items);
      setStories(items);
      setLastRefreshed(new Date().toISOString());
    } catch (e) {
      setError(e.message || "Failed to load stories");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []); // stable — reads period from ref

  useEffect(() => { fetchStories(false, timePeriodRef.current); }, [fetchStories]);

  const handleRefresh = () => { setRefreshing(true); fetchStories(true); };

  // Fix 2: period change always triggers fresh API fetch
  const handleTimePeriodChange = (period) => {
    setTimePeriod(period);
    setCardPage(0);
    timePeriodRef.current = period; // update ref immediately before async fetch
    fetchStories(true, period);
  };

  // View toggle
  const handleViewChange = (newView) => {
    setView(newView);
    try { localStorage.setItem(VIEW_KEY, newView); } catch {}
  };

  // Sort change (client-side only, no re-fetch)
  const handleSortChange = (newSort) => {
    setSortOrder(newSort);
    setCardPage(0);
  };

  // Reset card page when filters change
  useEffect(() => { setCardPage(0); }, [search, selectedCompanies, selectedImpactLevels, selectedFlaggedCos, selectedSentiments, selectedCategories, selectedGeoRegion, selectedGeoCountry, selectedPublishers, selectedPubTypes, selectedIndustries]);

  // Tier A: fetch portfolio sentiment
  useEffect(() => {
    const portfolioStories = stories.filter(s => s.portfolio_impact?.length > 0).slice(0, 30);
    portfolioStories.forEach(story => {
      const impact = story.portfolio_impact[0];
      if (!impact?.company_name) return;
      const key = `${story.brief_id}_${impact.company_name}`;
      if (sentimentFetchedRef.current.has(key)) return;
      sentimentFetchedRef.current.add(key);
      setPortfolioSentiments(prev => ({ ...prev, [key]: { loading: true } }));
      fetch(`${API}/sentiment/portfolio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          story_id: String(story.brief_id),
          company_name: impact.company_name,
          ai_summary: story.snippet || (story.description || "").slice(0, 500),
          portfolio_impact: impact.impact_description || "",
        }),
      })
        .then(r => r.ok ? r.json() : null)
        .then(d => { setPortfolioSentiments(prev => ({ ...prev, [key]: d ? { value: d.sentiment, reason: d.reason, loading: false } : { value: "NEUTRAL", reason: "", loading: false } })); })
        .catch(() => { setPortfolioSentiments(prev => ({ ...prev, [key]: { value: "NEUTRAL", reason: "", loading: false } })); });
    });
  }, [stories]);

  const getPortfolioSentiment = useCallback((story) => {
    if (!story.portfolio_impact?.length) return null;
    const impact = story.portfolio_impact[0];
    if (!impact?.company_name) return null;
    return portfolioSentiments[`${story.brief_id}_${impact.company_name}`] || null;
  }, [portfolioSentiments]);

  // ── Filter options ─────────────────────────────────────────────────────────
  const filterOptions = useMemo(() => {
    const portfolioCompanies = new Map();
    const publisherSet = new Set();
    const industrySet = new Set();
    const countrySet = new Set();

    stories.forEach(story => {
      story.portfolio_impact?.forEach(p => {
        if (p.company_name) {
          const ex = portfolioCompanies.get(p.company_name) || { count: 0, permalink: p.company_permalink || p.company_name.toLowerCase().replace(/\s+/g, "-") };
          ex.count++;
          portfolioCompanies.set(p.company_name, ex);
        }
      });

      (story.sources || []).forEach(src => {
        const name = src.publisher_name || src.source_name || src.name;
        if (name) publisherSet.add(name);
      });

      parseVerticalTags(story.vertical_tag)
        .filter(t => !["other","others"].includes(t.toLowerCase()) && !t.toLowerCase().startsWith("and "))
        .forEach(t => industrySet.add(t));

      const pt = normStr(story.primary_tag || "");
      pt.split(",").forEach(tag => {
        const trimmed = tag.trim();
        if (trimmed.includes(" - ")) {
          const geo = trimmed.split(" - ").slice(1).join(" - ").trim();
          if (geo && !["global", "other", "others"].includes(geo.toLowerCase())) countrySet.add(geo);
        }
      });
    });

    return {
      portfolioCompanies: Array.from(portfolioCompanies.entries()).map(([name, d]) => ({ name, ...d })).sort((a, b) => b.count - a.count),
      publishers: [...publisherSet].sort(),
      industries: [...industrySet].sort(),
      countries: [...countrySet].sort(),
    };
  }, [stories]);

  // ── Filter + sort stories ───────────────────────────────────────────────────
  const { portfolioStories, regularStories } = useMemo(() => {
    const portfolio = [], regular = [];
    stories.forEach(story => {
      const hasPortfolio = story.portfolio_impact && story.portfolio_impact.length > 0;
      let passes = true;

      // 1. Search filter
      if (search) {
        const q = search.toLowerCase();
        if (!story.title?.toLowerCase().includes(q) && !story.snippet?.toLowerCase().includes(q)) passes = false;
      }
      // 2. Company filter
      if (passes && selectedCompanies.length > 0) {
        const sc = (story.portfolio_impact || []).map(p => p.company_name);
        if (!selectedCompanies.some(c => sc.includes(c))) passes = false;
      }
      // 3. Impact filter
      if (passes && selectedImpactLevels.length > 0) {
        const scales = (story.portfolio_impact || []).map(p => (p.scale || "").toLowerCase());
        if (!selectedImpactLevels.some(lvl => scales.includes(lvl.toLowerCase()))) passes = false;
      }
      // 4. Flagged Companies filter
      if (passes && selectedFlaggedCos.length > 0) {
        const sc = (story.portfolio_impact || []).map(p => p.company_permalink || p.company_name?.toLowerCase().replace(/\s+/g, "-"));
        if (!selectedFlaggedCos.some(id => sc.includes(id))) passes = false;
      }
      // 5. Sentiment filter
      if (passes && selectedSentiments.length > 0) {
        let storySent;
        if (story.portfolio_impact?.length > 0) {
          const key = `${story.brief_id}_${story.portfolio_impact[0]?.company_name}`;
          const ps = portfolioSentiments[key];
          storySent = getSimplifiedSentiment((ps && !ps.loading) ? ps.value : getSentiment(`${story.title || ""} ${story.snippet || ""}`));
        } else {
          storySent = getSimplifiedSentiment(getSentiment(`${story.title || ""} ${story.snippet || ""}`));
        }
        if (!selectedSentiments.includes(storySent)) passes = false;
      }
      // 6. News Category filter
      if (passes && selectedCategories.length > 0) {
        const storyTagBases = normStr(story.primary_tag || "").split(",").map(t => {
          const full = t.trim();
          return full.includes(" - ") ? full.split(" - ")[0].trim().toLowerCase() : full.toLowerCase();
        }).filter(Boolean);
        if (!selectedCategories.some(cat => storyTagBases.includes(cat.toLowerCase()))) passes = false;
      }
      // 7. Geo/Market filter — Region + Country
      if (passes && selectedGeoRegion) {
        if (!matchesGeoRegion(story, selectedGeoRegion)) passes = false;
      }
      if (passes && selectedGeoCountry) {
        if (!matchesGeoCountry(story, selectedGeoCountry)) passes = false;
      }
      // 8. News Publisher filter
      if (passes && selectedPublishers.length > 0) {
        const storyPubs = (story.sources || []).map(s => s.publisher_name || s.source_name || s.name).filter(Boolean);
        if (!selectedPublishers.some(p => storyPubs.includes(p))) passes = false;
      }
      if (passes && selectedPubTypes.length > 0) {
        const storyPubTypes = (story.sources || []).map(s => s.publisher_type || s.source_type || "").filter(Boolean);
        if (!selectedPubTypes.some(pt => storyPubTypes.some(spt => spt.toLowerCase().includes(pt.toLowerCase())))) passes = false;
      }
      // 9. Industry filter
      if (passes && selectedIndustries.length > 0) {
        const storyIndustries = getStoryIndustries(story);
        if (!selectedIndustries.some(ind => storyIndustries.includes(ind))) passes = false;
      }

      // Custom date range filter
      if (passes && timePeriod.id === "custom" && (customDateStart || customDateEnd)) {
        const storyDate = new Date(story.first_seen_at || story.updated_at);
        if (customDateStart && storyDate < startOfDay(parseISO(customDateStart))) passes = false;
        if (customDateEnd) {
          const endDate = new Date(parseISO(customDateEnd));
          endDate.setHours(23, 59, 59, 999);
          if (storyDate > endDate) passes = false;
        }
      }

      if (!passes) return;

      if (hasPortfolio) {
        portfolio.push(story);
      } else {
        if (timePeriod.id !== "custom") {
          const d = new Date(story.first_seen_at || story.updated_at);
          const cutoff = startOfDay(subDays(new Date(), timePeriod.days));
          if (d >= cutoff) regular.push(story);
        } else {
          regular.push(story);
        }
      }
    });
    portfolio.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
    regular.sort((a, b)   => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
    return { portfolioStories: portfolio, regularStories: regular };
  }, [stories, search, selectedCompanies, selectedImpactLevels, selectedFlaggedCos, selectedSentiments, selectedCategories, selectedGeoRegion, selectedGeoCountry, selectedPublishers, selectedPubTypes, selectedIndustries, timePeriod, customDateStart, customDateEnd, portfolioSentiments]);

  const allFilteredStories = [...portfolioStories, ...regularStories];

  // ── Sort (Fix 4) ───────────────────────────────────────────────────────────
  const SENTIMENT_SORT_ORDER = { NEGATIVE: 0, NEUTRAL: 1, POSITIVE: 2 };

  const sortedStories = useMemo(() => {
    const getStorySentimentValue = (story) => {
      if (story.portfolio_impact?.length > 0) {
        const impact = story.portfolio_impact[0];
        if (impact?.company_name) {
          const ps = portfolioSentiments[`${story.brief_id}_${impact.company_name}`];
          if (ps && !ps.loading) return getSimplifiedSentiment(ps.value);
        }
      }
      return getSimplifiedSentiment(getSentiment(`${story.title || ""} ${story.snippet || ""}`));
    };

    const all = [...allFilteredStories];
    const byTime = (a, b) => new Date(b.first_seen_at || b.updated_at || 0) - new Date(a.first_seen_at || a.updated_at || 0);

    const IMPACT_ORDER = { HIGH: 0, MEDIUM: 1, LOW: 2 };
    const SENT_ORDER = { NEGATIVE: 0, POSITIVE: 1, NEUTRAL: 2 };

    switch (sortOrder) {
      case "risk_priority": {
        const attention = all.filter(isAttentionStory).sort(byTime).slice(0, 2);
        const attIds = new Set(attention.map(s => s.brief_id));
        const rest = all.filter(s => !attIds.has(s.brief_id));
        rest.sort((a, b) => {
          const dayA = new Date(a.first_seen_at || a.updated_at || 0).toISOString().slice(0, 10);
          const dayB = new Date(b.first_seen_at || b.updated_at || 0).toISOString().slice(0, 10);
          if (dayA !== dayB) return dayB.localeCompare(dayA);
          const aHasCompany = (a.portfolio_impact || []).some(p => p.company_name);
          const bHasCompany = (b.portfolio_impact || []).some(p => p.company_name);
          if (aHasCompany !== bHasCompany) return aHasCompany ? -1 : 1;
          const aImp = IMPACT_ORDER[getImpactLevel(a)] ?? 2;
          const bImp = IMPACT_ORDER[getImpactLevel(b)] ?? 2;
          if (aImp !== bImp) return aImp - bImp;
          const aSent = SENT_ORDER[getStorySentimentValue(a)] ?? 2;
          const bSent = SENT_ORDER[getStorySentimentValue(b)] ?? 2;
          if (aSent !== bSent) return aSent - bSent;
          return byTime(a, b);
        });
        return [...attention, ...rest];
      }
      case "latest_first":
        return all.sort(byTime);
      case "company_first": {
        const withCo = all.filter(s => (s.portfolio_impact || []).some(p => p.company_name));
        const macro = all.filter(s => !(s.portfolio_impact || []).some(p => p.company_name));
        withCo.sort((a, b) => {
          const aName = (a.portfolio_impact?.find(p => p.company_name)?.company_name || "").toLowerCase();
          const bName = (b.portfolio_impact?.find(p => p.company_name)?.company_name || "").toLowerCase();
          if (aName !== bName) return aName.localeCompare(bName);
          return byTime(a, b);
        });
        macro.sort(byTime);
        return [...withCo, ...macro];
      }
      case "industry_first": {
        all.sort((a, b) => {
          const aInd = (getStoryIndustries(a)[0] || "zzz").toLowerCase();
          const bInd = (getStoryIndustries(b)[0] || "zzz").toLowerCase();
          if (aInd !== bInd) return aInd.localeCompare(bInd);
          return byTime(a, b);
        });
        return all;
      }
      default:
        return allFilteredStories;
    }
  }, [allFilteredStories, sortOrder, portfolioSentiments]); // eslint-disable-line

  // Card view pagination
  const totalCardPages   = Math.ceil(sortedStories.length / PAGE_SIZE_CARDS);
  const cardPageStories  = sortedStories.slice(cardPage * PAGE_SIZE_CARDS, (cardPage + 1) * PAGE_SIZE_CARDS);
  const showStart        = sortedStories.length > 0 ? cardPage * PAGE_SIZE_CARDS + 1 : 0;
  const showEnd          = Math.min((cardPage + 1) * PAGE_SIZE_CARDS, sortedStories.length);

  // Table view reset key
  const tableResetKey = `${timePeriod.id}|${search}|${selectedCompanies.join(",")}|${selectedImpactLevels.join(",")}|${selectedCategories.join(",")}|${selectedSentiments.join(",")}|${selectedGeoRegion}|${sortOrder}`;

  // Header text
  const briefingTitle = timePeriod.id === "all" ? "Watchlist - All Briefings"
    : timePeriod.id === "today" ? "Watchlist Briefing"
    : timePeriod.id === "yesterday" ? "Yesterday's Watchlist"
    : `${timePeriod.label} Watchlist`;

  const hasFilters = !!(search || selectedCompanies.length || selectedImpactLevels.length || selectedFlaggedCos.length || selectedSentiments.length || selectedCategories.length || selectedGeoRegion || selectedGeoCountry || selectedPublishers.length || selectedPubTypes.length || selectedIndustries.length);
  const activeFilterCount = [
    search ? 1 : 0,
    timePeriod.id !== "today" ? 1 : 0,
    selectedCompanies.length > 0 ? 1 : 0,
    selectedImpactLevels.length > 0 ? 1 : 0,
    selectedFlaggedCos.length > 0 ? 1 : 0,
    selectedSentiments.length > 0 ? 1 : 0,
    selectedCategories.length > 0 ? 1 : 0,
    (selectedGeoRegion || selectedGeoCountry) ? 1 : 0,
    (selectedPublishers.length + selectedPubTypes.length) > 0 ? 1 : 0,
    selectedIndustries.length > 0 ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const clearFilters = () => {
    setSearch(""); setSelectedCompanies([]); setSelectedImpactLevels([]); setSelectedFlaggedCos([]);
    setSelectedSentiments([]); setSelectedCategories([]); setSelectedGeoRegion(""); setSelectedGeoCountry("");
    setSelectedPublishers([]); setSelectedPubTypes([]); setSelectedIndustries([]);
    setCustomDateStart(""); setCustomDateEnd("");
  };
  const toggleCompany = (name) => setSelectedCompanies(p => p.includes(name) ? p.filter(c => c !== name) : [...p, name]);
  const toggleArrayItem = (setter, val) => setter(p => p.includes(val) ? p.filter(v => v !== val) : [...p, val]);
  const handleStoryClick = (story) => {
    sessionStorage.setItem("wokelo_last_feed", "watchlist");
    // Store the computed sentiment for exact consistency on the story detail page
    const ps = getPortfolioSentiment(story);
    const hasPortfolio = story.portfolio_impact && story.portfolio_impact.length > 0;
    const kw = getSimplifiedSentiment(getSentiment(`${story.title || ""} ${story.snippet || ""}`));
    const feedSentiment = hasPortfolio ? getSimplifiedSentiment(ps?.value ?? kw) : kw;
    sessionStorage.setItem(`wokelo_feed_sentiment_${story.brief_id}`, feedSentiment);
    navigate(`/story/${story.brief_id}`, { state: { story } });
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", background: "#FFFFFF", height: "100%", overflow: "hidden" }}>

      {/* ── Left Filter Panel ── */}
      <aside data-testid="watchlist-filter-panel" style={{
        width: filterCollapsed ? "48px" : "220px",
        transition: "width 200ms ease",
        background: "#FFFFFF",
        flexShrink: 0,
        borderRight: "1px solid #DCDFE2",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
      }}>
        <div style={{
          height: "52px", display: "flex", alignItems: "center",
          justifyContent: filterCollapsed ? "center" : "space-between",
          padding: filterCollapsed ? "0" : "0 16px",
          borderBottom: "1px solid #DCDFE2", flexShrink: 0,
        }}>
          {!filterCollapsed && (
            <span style={{ fontSize: "9px", fontWeight: 700, color: "#6B6560", letterSpacing: "0.14em", textTransform: "uppercase" }}>
              Filters {activeFilterCount > 0 && <span style={{ color: "#60A5FA" }}>· {activeFilterCount}</span>}
            </span>
          )}
          <button onClick={toggleFilterPanel}
            className="w-8 h-8 rounded-md flex items-center justify-center transition-colors"
            style={{ background: "transparent" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F5F5F5"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            data-testid="filter-panel-toggle"
            title={filterCollapsed ? "Expand filters" : "Collapse filters"}>
            {filterCollapsed ? <Filter size={14} style={{ color: "#6B6560" }} /> : <ChevronLeft size={15} style={{ color: "#6B6560" }} />}
          </button>
          {filterCollapsed && activeFilterCount > 0 && (
            <span style={{ position: "absolute", top: "10px", right: "6px", background: "#60A5FA", color: "#181B20", borderRadius: "50%", width: "14px", height: "14px", fontSize: "9px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{activeFilterCount}</span>
          )}
        </div>

        {!filterCollapsed && (
          <div style={{ padding: "12px 14px 28px" }}>

            {/* Search */}
            <div style={{ marginBottom: "12px" }}>
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "#8C877F" }} />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search stories..." data-testid="filter-search"
                  className="w-full h-8 pl-8 pr-3 rounded text-[12px] focus:outline-none transition-all"
                  style={{ border: "1px solid #D4CFC9", background: "#FFFFFF", color: "#2A2420" }} />
              </div>
            </div>

            {/* 1. Time Period */}
            <FilterSection title="Time Period" count={timePeriod.id !== "today" ? 1 : 0}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginBottom: timePeriod.id === "custom" ? "8px" : "0" }}>
                {TIME_PERIODS.map(period => {
                  const isActive = timePeriod.id === period.id;
                  return (
                    <button key={period.id} onClick={() => handleTimePeriodChange(period)}
                      data-testid={`filter-period-${period.id}`}
                      style={{
                        padding: "3px 9px", borderRadius: "6px", fontSize: "11px",
                        fontWeight: isActive ? 600 : 400, cursor: "pointer", transition: "all 120ms",
                        background: isActive ? "#60A5FA" : "#FFFFFF",
                        color:      isActive ? "#181B20"  : "#4A4A4A",
                        border:     isActive ? "none" : "1px solid #DCDFE2",
                      }}>
                      {period.label}
                    </button>
                  );
                })}
              </div>
              {timePeriod.id === "custom" && (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <input type="date" value={customDateStart} onChange={e => setCustomDateStart(e.target.value)}
                    style={{ fontSize: "11px", padding: "4px 8px", border: "1px solid #D4CFC9", borderRadius: "4px", color: "#2A2420" }}
                    data-testid="custom-date-start" />
                  <input type="date" value={customDateEnd} onChange={e => setCustomDateEnd(e.target.value)}
                    style={{ fontSize: "11px", padding: "4px 8px", border: "1px solid #D4CFC9", borderRadius: "4px", color: "#2A2420" }}
                    data-testid="custom-date-end" />
                </div>
              )}
            </FilterSection>

            {/* 2. Watchlist Companies */}
            <FilterSection title="Watchlist Companies" count={selectedCompanies.length}>
              <div style={{ maxHeight: "150px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1px" }}>
                {filterOptions.portfolioCompanies.length === 0 ? (
                  <p style={{ fontSize: "11px", color: "#9AA0A6", fontStyle: "italic", padding: "4px 6px" }}>No watchlist companies</p>
                ) : filterOptions.portfolioCompanies.map(({ name, permalink }) => {
                  const isChecked = selectedCompanies.includes(name);
                  return (
                    <label key={name} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isChecked ? "#E9F1EC" : "transparent", transition: "background 100ms" }}
                      onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = "#F0EDE8"; }}
                      onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggleCompany(name)}
                        style={{ width: "13px", height: "13px", flexShrink: 0, cursor: "pointer", accentColor: "#1A1A1A" }} />
                      <CompanyLogo name={name} permalink={permalink} size={14} />
                      <span style={{ fontSize: "12px", color: "#2A2420", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: isChecked ? 500 : 400 }}>{name}</span>
                    </label>
                  );
                })}
              </div>
            </FilterSection>

            {/* 3. Watchlist Impact */}
            <FilterSection title="Watchlist Impact" count={selectedImpactLevels.length} defaultOpen={false}>
              <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                {PORTFOLIO_IMPACT_LEVELS.map(lvl => {
                  const isChecked = selectedImpactLevels.includes(lvl);
                  return (
                    <label key={lvl} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isChecked ? "#E9F1EC" : "transparent", transition: "background 100ms" }}
                      onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = "#F0EDE8"; }}
                      onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggleArrayItem(setSelectedImpactLevels, lvl)}
                        style={{ width: "13px", height: "13px", flexShrink: 0, cursor: "pointer", accentColor: "#1A1A1A" }} />
                      <span style={{ fontSize: "12px", color: "#2A2420", fontWeight: isChecked ? 500 : 400 }}>{lvl}</span>
                    </label>
                  );
                })}
              </div>
            </FilterSection>

            {/* 4. Flagged Companies */}
            <FilterSection title="Flagged Companies" count={selectedFlaggedCos.length} defaultOpen={false}>
              {flaggedCompanyList.length === 0 ? (
                <p style={{ fontSize: "11px", color: "#9AA0A6", fontStyle: "italic", padding: "4px 6px" }}>No flagged companies yet</p>
              ) : (
                <div style={{ maxHeight: "150px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1px" }}>
                  {flaggedCompanyList.map(({ id, name }) => {
                    const isChecked = selectedFlaggedCos.includes(id);
                    return (
                      <label key={id} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isChecked ? "#E9F1EC" : "transparent", transition: "background 100ms" }}
                        onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = "#F0EDE8"; }}
                        onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}>
                        <input type="checkbox" checked={isChecked} onChange={() => toggleArrayItem(setSelectedFlaggedCos, id)}
                          style={{ width: "13px", height: "13px", flexShrink: 0, cursor: "pointer", accentColor: "#1A1A1A" }} />
                        <CompanyLogo name={name} permalink={id} size={14} />
                        <span style={{ fontSize: "12px", color: "#2A2420", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: isChecked ? 500 : 400 }}>{name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </FilterSection>

            {/* 5. Sentiment */}
            <FilterSection title="Sentiment" count={selectedSentiments.length} defaultOpen={false}>
              <SentimentList
                sentiments={SENTIMENT_OPTIONS}
                selected={selectedSentiments}
                onToggle={s => setSelectedSentiments(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s])}
              />
            </FilterSection>

            {/* 6. News Category */}
            <FilterSection title="News Category" count={selectedCategories.length} defaultOpen={false}>
              <div style={{ maxHeight: "200px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1px" }}>
                {NEWS_CATEGORIES.map(cat => {
                  const isChecked = selectedCategories.includes(cat);
                  return (
                    <label key={cat} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isChecked ? "#E9F1EC" : "transparent", transition: "background 100ms" }}
                      onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = "#F0EDE8"; }}
                      onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggleArrayItem(setSelectedCategories, cat)}
                        style={{ width: "13px", height: "13px", flexShrink: 0, cursor: "pointer", accentColor: "#1A1A1A" }} />
                      <span style={{ fontSize: "12px", color: "#2A2420", fontWeight: isChecked ? 500 : 400 }}>{cat}</span>
                    </label>
                  );
                })}
              </div>
            </FilterSection>

            {/* 7. Geo / Market */}
            <FilterSection title="Geo / Market" count={(selectedGeoRegion ? 1 : 0) + (selectedGeoCountry ? 1 : 0)} defaultOpen={false}>
              <p style={{ fontSize: "10px", fontWeight: 600, color: "#9AA0A6", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Region</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "1px", marginBottom: "8px" }}>
                {GEO_REGIONS.map(region => {
                  const isActive = selectedGeoRegion === region;
                  return (
                    <button key={region}
                      onClick={() => setSelectedGeoRegion(isActive ? "" : region)}
                      style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isActive ? "#E9F1EC" : "transparent", border: "none", width: "100%", textAlign: "left", transition: "background 100ms" }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#F0EDE8"; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}>
                      <span style={{ width: "13px", height: "13px", borderRadius: "50%", border: isActive ? "none" : "1px solid #D1D5DB", background: isActive ? "#60A5FA" : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {isActive && <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#181B20" }} />}
                      </span>
                      <span style={{ fontSize: "12px", color: isActive ? "#1A1A1A" : "#6B7280", fontWeight: isActive ? 500 : 400 }}>{region}</span>
                    </button>
                  );
                })}
              </div>
              {filterOptions.countries.length > 0 && (
                <>
                  <p style={{ fontSize: "10px", fontWeight: 600, color: "#9AA0A6", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Country</p>
                  <div style={{ maxHeight: "120px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1px" }}>
                    {filterOptions.countries.map(country => {
                      const isActive = selectedGeoCountry === country;
                      return (
                        <button key={country}
                          onClick={() => setSelectedGeoCountry(isActive ? "" : country)}
                          style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isActive ? "#E9F1EC" : "transparent", border: "none", width: "100%", textAlign: "left", transition: "background 100ms" }}
                          onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#F0EDE8"; }}
                          onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}>
                          <span style={{ width: "13px", height: "13px", borderRadius: "50%", border: isActive ? "none" : "1px solid #D1D5DB", background: isActive ? "#60A5FA" : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {isActive && <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#181B20" }} />}
                          </span>
                          <span style={{ fontSize: "12px", color: isActive ? "#1A1A1A" : "#6B7280", fontWeight: isActive ? 500 : 400 }}>{country}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </FilterSection>

            {/* 8. News Publisher */}
            <FilterSection title="News Publisher" count={selectedPublishers.length + selectedPubTypes.length} defaultOpen={false}>
              <p style={{ fontSize: "10px", fontWeight: 600, color: "#9AA0A6", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Publisher Type</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "1px", marginBottom: "8px" }}>
                {PUBLISHER_TYPES.map(pt => {
                  const isChecked = selectedPubTypes.includes(pt);
                  return (
                    <label key={pt} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isChecked ? "#E9F1EC" : "transparent", transition: "background 100ms" }}
                      onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = "#F0EDE8"; }}
                      onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggleArrayItem(setSelectedPubTypes, pt)}
                        style={{ width: "13px", height: "13px", flexShrink: 0, cursor: "pointer", accentColor: "#1A1A1A" }} />
                      <span style={{ fontSize: "12px", color: "#2A2420", fontWeight: isChecked ? 500 : 400 }}>{pt}</span>
                    </label>
                  );
                })}
              </div>
              {filterOptions.publishers.length > 0 && (
                <>
                  <p style={{ fontSize: "10px", fontWeight: 600, color: "#9AA0A6", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Publisher Name</p>
                  <div style={{ maxHeight: "140px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1px" }}>
                    {filterOptions.publishers.map(pub => {
                      const isChecked = selectedPublishers.includes(pub);
                      return (
                        <label key={pub} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isChecked ? "#E9F1EC" : "transparent", transition: "background 100ms" }}
                          onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = "#F0EDE8"; }}
                          onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}>
                          <input type="checkbox" checked={isChecked} onChange={() => toggleArrayItem(setSelectedPublishers, pub)}
                            style={{ width: "13px", height: "13px", flexShrink: 0, cursor: "pointer", accentColor: "#1A1A1A" }} />
                          <span style={{ fontSize: "12px", color: "#2A2420", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: isChecked ? 500 : 400 }}>{pub}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </FilterSection>

            {/* 9. Industry */}
            <FilterSection title="Industry" count={selectedIndustries.length} defaultOpen={false}>
              <div style={{ maxHeight: "180px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1px" }}>
                {INDUSTRY_VERTICALS.map(ind => {
                  const isChecked = selectedIndustries.includes(ind);
                  return (
                    <label key={ind} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 6px", borderRadius: "4px", cursor: "pointer", background: isChecked ? "#E9F1EC" : "transparent", transition: "background 100ms" }}
                      onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = "#F0EDE8"; }}
                      onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggleArrayItem(setSelectedIndustries, ind)}
                        style={{ width: "13px", height: "13px", flexShrink: 0, cursor: "pointer", accentColor: "#1A1A1A" }} />
                      <span style={{ fontSize: "12px", color: "#2A2420", fontWeight: isChecked ? 500 : 400 }}>{ind}</span>
                    </label>
                  );
                })}
              </div>
            </FilterSection>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button onClick={clearFilters}
                className="w-full h-8 rounded text-[11px] font-medium transition-all flex items-center justify-center gap-1.5 mt-2"
                style={{ border: "1px solid #D4CFC9", color: "#6B6560", background: "transparent" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#F5F5F5"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                data-testid="clear-filters-btn">
                <X size={11} />Clear filters
              </button>
            )}

          </div>
        )}
      </aside>

      {/* ── Right Content ── */}
      <main style={{ flex: 1, padding: "24px", minWidth: 0, height: "100%", overflowY: "auto" }}>

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[32px] font-bold text-[#0A0A0A] pb-2 border-b-2 border-[#3A3A3A] inline-flex items-center gap-3"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              {briefingTitle} <Eye size={22} className="text-[#60A5FA]" />
            </h1>
            <p className="text-[13px] text-[#6B7280] mt-2">
              {loading ? "Loading..." : (
                <>
                  <span className="font-semibold text-[#0A0A0A]">{sortedStories.length}</span> stories
                  {portfolioStories.length > 0 && <span style={{ color: "#60A5FA" }}> · {portfolioStories.length} companies</span>}
                  {" · "}Last updated {getRelativeTime(lastRefreshed)}
                </>
              )}
            </p>
          </div>

          {/* Controls: Sort + View Toggle + Refresh */}
          <div className="flex items-center gap-2">
            {/* Fix 4: Sort dropdown */}
            <SortDropdown options={SORT_OPTIONS} selected={sortOrder} onChange={handleSortChange} />

            {/* View toggle */}
            <div className="flex items-center" style={{ border: "1px solid #DCDFE2", borderRadius: "6px", overflow: "hidden" }}>
              <button onClick={() => handleViewChange("card")}
                className="flex items-center justify-center"
                style={{ width: "34px", height: "34px", background: view === "card" ? "#60A5FA" : "#FFFFFF", color: view === "card" ? "#181B20" : "#9AA0A6", borderRight: "1px solid #E5E7EB", transition: "background 150ms ease, color 150ms ease" }}
                title="Card view" data-testid="view-toggle-card">
                <LayoutGrid size={15} />
              </button>
              <button onClick={() => handleViewChange("table")}
                className="flex items-center justify-center"
                style={{ width: "34px", height: "34px", background: view === "table" ? "#60A5FA" : "#FFFFFF", color: view === "table" ? "#181B20" : "#9AA0A6", transition: "background 150ms ease, color 150ms ease" }}
                title="Table view" data-testid="view-toggle-table">
                <List size={15} />
              </button>
            </div>

            <button onClick={handleRefresh} disabled={refreshing || loading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-[#DCDFE2] rounded-md text-[13px] font-medium text-[#3A3A3A] hover:bg-[#F4F5F6] disabled:opacity-50 transition-colors"
              data-testid="refresh-btn">
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 p-4 rounded-md text-[13px] flex items-center gap-3"
            style={{ background: "#FEE2E2", border: "1px solid #DC2626", color: "#DC2626" }}>
            <span>{error}</span>
            <button onClick={handleRefresh} className="ml-auto font-semibold hover:underline">Retry</button>
          </div>
        )}

        {/* Story Feed */}
        {loading ? (
          view === "card" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <StoryCardSkeleton />
              <div className="story-grid">{[...Array(4)].map((_, i) => <StoryCardSkeleton key={i} />)}</div>
            </div>
          ) : (
            <div className="bg-white rounded-md overflow-hidden" style={{ border: "1px solid #E5E7EB" }}>
              <div className="animate-pulse p-4 space-y-3">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="skeleton h-4 flex-1" /><div className="skeleton h-4 w-16" />
                    <div className="skeleton h-4 w-20" /><div className="skeleton h-4 w-24" />
                    <div className="skeleton h-4 w-12" />
                  </div>
                ))}
              </div>
            </div>
          )
        ) : sortedStories.length === 0 ? (
          /* Empty state */
          <div className="bg-white rounded-md border border-[#DCDFE2] p-12 text-center" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
            <Search size={32} className="text-[#DCDFE2] mx-auto mb-3" />
            <p className="font-semibold text-[#3A3A3A] text-[15px]">
              {hasFilters
                ? "No stories match your filters"
                : `No stories found for this time period`}
            </p>
            <p className="text-[13px] text-[#9AA0A6] mt-1 italic">
              {hasFilters ? "Try adjusting your filters" : "Try selecting a wider time range"}
            </p>
            {hasFilters && (
              <button onClick={clearFilters} className="mt-3 text-[13px] font-medium text-[#60A5FA] hover:underline transition-colors" data-testid="empty-clear-filters">
                Clear all filters
              </button>
            )}
          </div>
        ) : view === "card" ? (
          /* Card view — uniform 2-column grid */
          <div>
            {/* Showing X-Y of Z counter */}
            <div className="flex items-center justify-end mb-3">
              <span className="text-[11px] text-[#9AA0A6]" data-testid="card-showing-count">
                Showing {showStart}–{showEnd} of {sortedStories.length} stories
              </span>
            </div>

            {/* All cards in uniform 2-column grid */}
            {cardPageStories.length > 0 && (
              <div className="story-grid" style={{ alignItems: "start" }}>
                {cardPageStories.map(story => (
                  <StoryCard key={story.brief_id} story={story} onClick={handleStoryClick} portfolioSentiment={getPortfolioSentiment(story)} />
                ))}
              </div>
            )}

            {/* Pagination controls */}
            {totalCardPages > 1 && (
              <div className="flex items-center justify-between mt-6 px-2" data-testid="card-pagination">
                <button
                  onClick={() => setCardPage(p => Math.max(0, p - 1))}
                  disabled={cardPage === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-40 transition-colors hover:bg-[#F4F5F6]"
                  style={{ border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#3A3A3A" }}
                  data-testid="card-prev-page"
                >
                  ← Previous
                </button>
                <span className="text-[12px] text-[#9AA0A6]" data-testid="card-page-indicator">
                  Page {cardPage + 1} of {totalCardPages}
                </span>
                <button
                  onClick={() => setCardPage(p => Math.min(totalCardPages - 1, p + 1))}
                  disabled={cardPage >= totalCardPages - 1}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-40 transition-colors hover:bg-[#F4F5F6]"
                  style={{ border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#3A3A3A" }}
                  data-testid="card-next-page"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Table view */
          <NewsTable
            key={tableResetKey}
            stories={sortedStories}
            onStoryClick={handleStoryClick}
            sortMode={sortOrder}
            getStorySentiment={getPortfolioSentiment}
            showAttention={timePeriod.id === "all"}
          />
        )}
      </main>
    </div>
  );
}
